import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ManualEntryDialog } from "../components/dividends/ManualEntryDialog";
import { MonthDetailModal } from "../components/dividends/MonthDetailModal";
import { MonthGrid } from "../components/dividends/MonthGrid";
import { YearSidebar } from "../components/dividends/YearSidebar";
import { useDividendCalendarRange, useRefreshDividends } from "../hooks/dividends";
import type { InvestmentKind } from "../lib/dividends";
import { type MonthTileData, aggregateByMonth, aggregateByYear } from "../lib/dividends-utils";

const FILTER_KINDS: { value: InvestmentKind; key: string }[] = [
  { value: "AKTIE", key: "dividends.filter.aktien" },
  { value: "ETF", key: "dividends.filter.etf" },
  { value: "KRYPTO", key: "dividends.filter.krypto" },
];

const DEFAULT_HISTORY_YEARS = 5;

function currentYear(): number {
  return new Date().getFullYear();
}

function rangeFor(historyYears: number): { from: string; to: string } {
  const cy = currentYear();
  return {
    from: `${cy - historyYears}-01-01`,
    to: `${cy + 1}-12-31`,
  };
}

export function DividendsPage() {
  const { t } = useTranslation();
  const [historyYears, setHistoryYears] = useState(DEFAULT_HISTORY_YEARS);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear());
  const [modalMonth, setModalMonth] = useState<string | null>(null);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [selectedKinds, setSelectedKinds] = useState<Set<InvestmentKind>>(
    () => new Set<InvestmentKind>(["AKTIE", "ETF", "KRYPTO"]),
  );

  const query = useDividendCalendarRange(rangeFor(historyYears));
  const refresh = useRefreshDividends();
  const entries = useMemo(
    () =>
      (query.data?.entries ?? []).filter(
        (e) => !e.investmentKind || selectedKinds.has(e.investmentKind),
      ),
    [query.data, selectedKinds],
  );

  const yearlyTotals = useMemo(() => aggregateByYear(entries), [entries]);
  const yearsDescending = useMemo(
    () => [...yearlyTotals].sort((a, b) => b.year - a.year),
    [yearlyTotals],
  );
  const { upcoming, past } = useMemo(
    () => aggregateByMonth(entries, selectedYear, new Date()),
    [entries, selectedYear],
  );

  const activeMonthTile: MonthTileData | null = useMemo(() => {
    if (!modalMonth) return null;
    return [...upcoming, ...past].find((m) => m.month === modalMonth) ?? null;
  }, [modalMonth, upcoming, past]);

  function toggleKind(kind: InvestmentKind): void {
    setSelectedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  const canLoadOlder = historyYears < 50; // hard cap

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_reports")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("dividends.page.title")}</h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {entries.length} {t("dividends.entries_plural", { defaultValue: "Eintraege" })}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-50"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
          >
            {refresh.isPending ? t("common.submitting") : t("dividends.refresh")}
          </button>
          <button
            type="button"
            onClick={() => setShowManualDialog(true)}
            className="px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            + {t("dividends.manual_entry")}
          </button>
        </div>
      </div>

      {/* Kind-Filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: "var(--muted)" }}>
          {t("dividends.filter.label")}:
        </span>
        <div className="flex gap-1">
          {FILTER_KINDS.map((f) => {
            const active = selectedKinds.has(f.value);
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => toggleKind(f.value)}
                aria-pressed={active}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border"
                style={
                  active
                    ? { background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" }
                    : {
                        background: "var(--card)",
                        color: "var(--muted)",
                        borderColor: "var(--border)",
                      }
                }
              >
                {t(f.key)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Split-View */}
      {query.isLoading ? (
        <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
          {t("common.loading", { defaultValue: "Lädt…" })}
        </div>
      ) : entries.length === 0 ? (
        <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
          {t("dividends.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
          <YearSidebar
            years={yearsDescending}
            selectedYear={selectedYear}
            onSelectYear={setSelectedYear}
            onLoadOlder={canLoadOlder ? () => setHistoryYears((y) => y + 5) : undefined}
          />
          <MonthGrid
            year={selectedYear}
            upcoming={upcoming}
            past={past}
            onSelectMonth={setModalMonth}
          />
        </div>
      )}

      {activeMonthTile && (
        <MonthDetailModal
          data={activeMonthTile}
          onClose={() => setModalMonth(null)}
          onAddManual={() => {
            setModalMonth(null);
            setShowManualDialog(true);
          }}
        />
      )}

      {showManualDialog && <ManualEntryDialog onClose={() => setShowManualDialog(false)} />}
    </section>
  );
}
